/**
 * Shop by category carousel component for mobile view.
 * Handles touch sliding, previous/next navigation, and active dots indication.
 */
class ShopByCategoryCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLButtonElement | null} */
  #prev = null;

  /** @type {HTMLButtonElement | null} */
  #next = null;

  /** @type {HTMLButtonElement[]} */
  #dots = [];

  /** @type {HTMLElement[]} */
  #items = [];

  /** @type {number | null} */
  #raf = null;

  connectedCallback() {
    this.#track = this.querySelector('[data-category-track]');
    this.#prev = this.querySelector('[data-category-prev]');
    this.#next = this.querySelector('[data-category-next]');
    this.#dots = Array.from(this.querySelectorAll('[data-category-dot]'));
    this.#items = Array.from(this.querySelectorAll('.shop-by-category__item'));

    if (!this.#track || this.#items.length === 0) return;

    this.#prev?.addEventListener('click', this.#onPrev);
    this.#next?.addEventListener('click', this.#onNext);

    this.#dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.#scrollToIndex(index));
    });

    this.#track.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });

    this.#updateDots();
  }

  disconnectedCallback() {
    this.#prev?.removeEventListener('click', this.#onPrev);
    this.#next?.removeEventListener('click', this.#onNext);
    this.#track?.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);

    if (this.#raf != null) {
      cancelAnimationFrame(this.#raf);
      this.#raf = null;
    }
  }

  #onPrev = () => {
    this.#scrollStep(-1);
  };

  #onNext = () => {
    this.#scrollStep(1);
  };

  #getItemStep() {
    const item = this.#items[0];
    if (!item || !this.#track) return 0;
    const styles = window.getComputedStyle(this.#track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 16;
    return item.getBoundingClientRect().width + gap;
  }

  #scrollStep(direction) {
    const track = this.#track;
    if (!track) return;

    const step = this.#getItemStep();
    if (step <= 0) return;

    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    if (maxScroll <= 0) return;

    let nextLeft = track.scrollLeft + direction * step;

    if (nextLeft < -10) {
      nextLeft = maxScroll;
    } else if (nextLeft > maxScroll + 10) {
      nextLeft = 0;
    } else {
      nextLeft = Math.max(0, Math.min(maxScroll, nextLeft));
    }

    track.scrollTo({
      left: nextLeft,
      behavior: 'smooth'
    });
  }

  #scrollToIndex(index) {
    if (!this.#track) return;

    if (index === 0) {
      this.#track.scrollTo({
        left: 0,
        behavior: 'smooth'
      });
      return;
    }

    const maxScroll = Math.max(0, this.#track.scrollWidth - this.#track.clientWidth);

    if (index === this.#dots.length - 1) {
      this.#track.scrollTo({
        left: maxScroll,
        behavior: 'smooth'
      });
      return;
    }

    const item = this.#items[index];
    if (!item) return;

    const trackRect = this.#track.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const targetOffset = Math.min(
      maxScroll,
      Math.max(0, itemRect.left - trackRect.left + this.#track.scrollLeft)
    );

    this.#track.scrollTo({
      left: targetOffset,
      behavior: 'smooth'
    });
  }

  #onScroll = () => {
    if (this.#raf != null) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = null;
      this.#updateDots();
    });
  };

  #updateDots() {
    if (this.#dots.length === 0 || !this.#track) return;

    const scrollLeft = this.#track.scrollLeft;
    const maxScroll = Math.max(0, this.#track.scrollWidth - this.#track.clientWidth);
    const step = this.#getItemStep();
    if (step <= 0) return;

    let activeIndex;
    if (maxScroll > 0 && scrollLeft >= maxScroll - 4) {
      activeIndex = this.#dots.length - 1;
    } else {
      activeIndex = Math.min(
        this.#dots.length - 1,
        Math.max(0, Math.round(scrollLeft / step))
      );
    }

    this.#dots.forEach((dot, index) => {
      const isActive = index === activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }
}

if (!customElements.get('shop-by-category-carousel')) {
  customElements.define('shop-by-category-carousel', ShopByCategoryCarousel);
}
